/**
 * httpAbort — link a caller-provided AbortSignal with an internal timeout
 * controller into a single abortable surface.
 *
 * Extracted from serviceLayerClient.ts on 2026-04-25 (Bridge regression
 * forensics, FR-SL-CLIENT-ABORT-LEAK). The previous design constructed a
 * controller only when `timeoutMs` was set and unconditionally overrode
 * the caller's `init.signal` via `signal: controller?.signal` in the
 * fetch options — silently discarding any signal the caller (typically
 * a React Query queryFn) provided. The result on the bridge:
 *
 *   1. Component mounts a query, queryFn fires fetches with timeoutMs.
 *   2. User navigates; React Query disposes the observer, calls cancel
 *      on the queryFn-context signal — but the underlying fetch never
 *      sees that signal, so the request keeps running.
 *   3. Our timeout fires later (e.g. 10s for missions/leads). The fetch
 *      finally aborts.
 *   4. Rejection chains up through gateways. Some have try/catch that
 *      swallow it; some don't. The ones that don't reach React Query's
 *      retryer (`s` in helpers.js), but the originating observer is
 *      gone, so the rejection is unhandled.
 *   5. Browser logs "Uncaught (in promise) AbortError: signal is aborted
 *      without reason at serviceLayerClient.ts:139". One per orphaned
 *      request — 80+ on a Wochenplanung→Bridge transition with 100
 *      missions x parallel listLeads.
 *
 * Fix shape (this module):
 *   - One internal AbortController observes BOTH sources.
 *   - `timedOut()` reports whether the abort came from our timer (so the
 *     consumer can convert into a typed `ServiceHttpError('TIMEOUT')`)
 *     vs the caller's signal (re-throw raw AbortError so React Query's
 *     queryFn-context recognises it as a cancellation and discards the
 *     result without console flooding).
 *   - `cleanup()` unwinds the timer + signal listener exactly once,
 *     called on both the success and the error path.
 */

export interface LinkedAbortHandle {
  /** AbortSignal to pass through to fetch. `undefined` when neither
   *  timeoutMs nor callerSignal was provided. */
  signal: AbortSignal | undefined;
  /** True when the internal timer fired the abort. Use to decide whether
   *  to re-throw raw AbortError (caller cancellation) vs raise a typed
   *  TIMEOUT error (server-too-slow). */
  timedOut: () => boolean;
  /** Idempotent: clears the timer (if any) and removes the caller-signal
   *  abort listener (if any). Safe to call multiple times. */
  cleanup: () => void;
}

export interface LinkedAbortInput {
  timeoutMs?: number;
  callerSignal?: AbortSignal | null;
}

export function createLinkedAbortHandle({
  timeoutMs,
  callerSignal,
}: LinkedAbortInput): LinkedAbortHandle {
  // Fast path: neither source provided → return inert handle, no
  // controller allocation, no listener attached.
  if (typeof AbortController === 'undefined' || (!timeoutMs && !callerSignal)) {
    return {
      signal: undefined,
      timedOut: () => false,
      cleanup: () => undefined,
    };
  }

  const controller = new AbortController();
  let timedOutByUs = false;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  let onCallerAbort: (() => void) | undefined;
  let cleanedUp = false;

  if (timeoutMs && timeoutMs > 0) {
    timeoutHandle = setTimeout(() => {
      timedOutByUs = true;
      controller.abort();
    }, timeoutMs);
  }

  if (callerSignal) {
    if (callerSignal.aborted) {
      // Caller's signal already aborted before we wired the listener
      // (e.g. queryFn invoked synchronously after observer cancellation).
      // Reflect immediately so fetch never even starts.
      controller.abort();
    } else {
      onCallerAbort = () => controller.abort();
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  return {
    signal: controller.signal,
    timedOut: () => timedOutByUs,
    cleanup: () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      if (onCallerAbort && callerSignal) {
        callerSignal.removeEventListener('abort', onCallerAbort);
      }
    },
  };
}
